import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  FileSpreadsheet,
  Inbox,
  MessageSquareWarning,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api, { apiErrorMessage } from "@/lib/api";
import { formatMoney, formatPeriod } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const KPIS = [
  { key: "total_commission", label: "Provisionen gesamt", icon: TrendingUp, money: true },
  { key: "active_customer_count", label: "Aktive Kunden", icon: Users },
  { key: "report_count", label: "Reports erfasst", icon: FileSpreadsheet },
  { key: "pending_reports", label: "Pending Postbox", icon: Inbox },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/dashboard/summary")
      .then((r) => !cancelled && setData(r.data))
      .catch((err) => !cancelled && setError(apiErrorMessage(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Übersicht">
        Monatliche Provisionsanalyse, KPIs und Top-Kunden auf einen Blick.
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-8">
        {error && (
          <div className="border border-rose-200 bg-rose-50 text-rose-700 text-sm rounded-sm px-3 py-2">
            {error}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {KPIS.map(({ key, label, icon: Icon, money }) => {
            const value = data?.[key];
            const display = money ? formatMoney(value) : value ?? "—";
            return (
              <div
                key={key}
                className="kpi-card p-5 rounded-sm"
                data-testid={`kpi-${key}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="kbd-label">{label}</span>
                  <Icon size={18} strokeWidth={1.4} className="text-slate-400" />
                </div>
                <div className="font-display text-3xl font-bold text-slate-900 tabular-nums">
                  {loading ? "—" : display}
                </div>
              </div>
            );
          })}
        </div>

        {/* Discrepancies / Claims signal row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="kpi-card p-5 rounded-sm flex items-start gap-4" data-testid="kpi-discrepancies">
            <div className="w-10 h-10 rounded-sm bg-rose-50 text-rose-600 flex items-center justify-center">
              <AlertTriangle size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="kbd-label">Soll/Ist-Abweichungen</div>
              <div className="font-display text-2xl font-bold mt-1">
                {data?.discrepancy_count ?? 0}
              </div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">
                Summe: {formatMoney(data?.total_discrepancy_amount || 0, true)}
              </div>
            </div>
          </div>
          <div className="kpi-card p-5 rounded-sm flex items-start gap-4" data-testid="kpi-open-claims">
            <div className="w-10 h-10 rounded-sm bg-amber-50 text-amber-600 flex items-center justify-center">
              <MessageSquareWarning size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="kbd-label">Offene Claims</div>
              <div className="font-display text-2xl font-bold mt-1">
                {data?.open_claims ?? 0}
              </div>
              <Link
                to="/claims"
                className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2 mt-1 inline-flex items-center gap-1"
              >
                Claims verwalten <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
          <div className="kpi-card p-5 rounded-sm flex items-start gap-4" data-testid="kpi-pending-postbox">
            <div className="w-10 h-10 rounded-sm bg-blue-50 text-blue-600 flex items-center justify-center">
              <Activity size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="kbd-label">Postbox wartend</div>
              <div className="font-display text-2xl font-bold mt-1">
                {data?.pending_reports ?? 0}
              </div>
              <Link
                to="/postbox"
                className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2 mt-1 inline-flex items-center gap-1"
              >
                Postbox öffnen <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        </div>

        {/* Chart + top customers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="kbd-label">Monatsumsatz</div>
                <h3 className="font-display text-lg font-bold mt-0.5">Provisionen je Monat</h3>
              </div>
              <div className="flex gap-3 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 bg-slate-900 rounded-sm" /> Payment
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 bg-violet-500 rounded-sm" /> License
                </span>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={(data?.monthly || []).map((m) => ({
                  ...m,
                  label: formatPeriod(m.period_month, m.period_label),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    stroke="#cbd5e1"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    stroke="#cbd5e1"
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k €`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                    formatter={(value) => formatMoney(value)}
                  />
                  <Bar dataKey="payment" stackId="a" fill="#0f172a" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="license" stackId="a" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm" data-testid="top-customers-table">
            <div className="p-5 border-b border-slate-100">
              <div className="kbd-label">Top 10 Kunden</div>
              <h3 className="font-display text-lg font-bold mt-0.5">Gesamtprovisionen</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {(data?.top_customers || []).map((c, idx) => (
                <Link
                  key={c.id}
                  to={`/customers/${c.id}`}
                  className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`top-customer-${idx}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-xs text-slate-400 w-5">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-medium text-slate-900 truncate">{c.name}</span>
                  </div>
                  <span className="font-mono text-sm font-semibold text-slate-900 whitespace-nowrap">
                    {formatMoney(c.total_commission_to_date)}
                  </span>
                </Link>
              ))}
              {(data?.top_customers || []).length === 0 && !loading && (
                <div className="px-5 py-8 text-center text-sm text-slate-500">
                  Noch keine Daten – bitte einen Report hochladen.
                </div>
              )}
            </div>
          </div>
        </div>

        <TopCustomersByScope />
      </div>
    </div>
  );
}

function TopCustomersByScope() {
  const [scope, setScope] = useState("all"); // all | month | year
  const [type, setType] = useState("any"); // any | payment | license
  const [periods, setPeriods] = useState({ months: [], years: [] });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [data, setData] = useState({ customers: [], scope_label: "—", total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/periods")
      .then((r) => {
        setPeriods(r.data);
        if (r.data.months?.length) setSelectedMonth(r.data.months[0]);
        if (r.data.years?.length) setSelectedYear(r.data.years[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (scope === "month" && selectedMonth) params.period_month = selectedMonth;
    else if (scope === "year" && selectedYear) params.year = selectedYear;
    if (type !== "any") params.type = type;
    api
      .get("/dashboard/top-customers", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ customers: [], scope_label: "—", total: 0 }))
      .finally(() => setLoading(false));
  }, [scope, type, selectedMonth, selectedYear]);

  const chartData = useMemo(
    () => data.customers.slice(0, 15).map((c) => ({ name: c.name, total: c.total, id: c.id })),
    [data.customers]
  );
  const longestName = useMemo(
    () => Math.min(220, Math.max(120, ...chartData.map((c) => c.name.length * 7))),
    [chartData]
  );
  const chartHeight = Math.max(220, chartData.length * 32 + 40);

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm"
      data-testid="top-customers-scope-section"
    >
      <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex-1">
          <div className="kbd-label">Top-Kunden nach Periode</div>
          <h3 className="font-display text-lg font-bold mt-0.5">
            {data.scope_label}
            <span className="ml-2 font-mono text-sm font-semibold text-slate-500">
              {formatMoney(data.total)}
            </span>
          </h3>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-[120px] h-9" data-testid="scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Gesamt</SelectItem>
              <SelectItem value="year">Jahr</SelectItem>
              <SelectItem value="month">Monat</SelectItem>
            </SelectContent>
          </Select>
          {scope === "month" && periods.months.length > 0 && (
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[160px] h-9" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periods.months.map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatPeriod(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {scope === "year" && periods.years.length > 0 && (
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[100px] h-9" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periods.years.map((y) => (
                  <SelectItem key={y} value={y}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[160px] h-9" data-testid="type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Beide Typen</SelectItem>
              <SelectItem value="payment">Zahlungs-Provision</SelectItem>
              <SelectItem value="license">Lizenz-Provision</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <div className="px-5 py-10 text-center text-slate-500 text-sm">Lade Daten …</div>
      ) : chartData.length === 0 ? (
        <div className="px-5 py-12 text-center text-slate-500 text-sm">
          Keine Daten für diese Periode.
        </div>
      ) : (
        <div className="p-5" style={{ width: "100%", height: chartHeight }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 32, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#64748b" }}
                stroke="#cbd5e1"
                tickFormatter={(v) => `${(v / 1000).toFixed(v >= 1000 ? 1 : 2)}k €`}
              />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 11, fill: "#0f172a" }}
                stroke="#cbd5e1"
                width={longestName}
                interval={0}
              />
              <Tooltip
                contentStyle={{
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: 4,
                  fontSize: 12,
                }}
                formatter={(value) => [formatMoney(value), "Provision"]}
              />
              <Bar dataKey="total" radius={[0, 2, 2, 0]}>
                {chartData.map((entry, idx) => (
                  <Cell
                    key={entry.id}
                    fill={idx === 0 ? "#0f172a" : idx < 3 ? "#334155" : "#64748b"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
